import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, ExternalLink } from "lucide-react";

const Login = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { whopLogin, handleWhopToken, isAuthenticated } = useAuth();
  const [whopLoading, setWhopLoading] = useState(false);

  useEffect(() => {
    const token = searchParams.get("token");
    const whopError = searchParams.get("whop_error");
    if (token) {
      handleWhopToken(token);
      setSearchParams({}, { replace: true });
      navigate("/", { replace: true });
    } else if (whopError) {
      const msg = whopError.replace(/_/g, " ");
      toast.error(`Login failed: ${msg}`);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, handleWhopToken, navigate]);

  useEffect(() => {
    if (isAuthenticated) navigate("/", { replace: true });
  }, [isAuthenticated, navigate]);

  const handleWhopLogin = () => { setWhopLoading(true); whopLogin(); };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ background: 'var(--bg-primary)' }}>

      {/* ─── Logo + Brand ─── */}
      <div className="flex flex-col items-center mb-8">
        {/* Logo with glow */}
        <div className="relative mb-5">
          <div className="absolute inset-0 rounded-full blur-3xl opacity-20"
            style={{ background: 'radial-gradient(circle, var(--accent-teal), transparent 70%)', transform: 'scale(2.5)' }} />
          <img src="/logo.png" alt="Edge Bot" className="relative w-16 h-16 rounded-2xl" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>Edge Bot</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>by Probability Lab</p>
        <p className="text-sm mt-2" style={{ color: 'var(--accent-teal)', opacity: 0.8 }}>Automated BTC Trading on Polymarket</p>
      </div>

      {/* ─── Login Card ─── */}
      <div className="w-full max-w-sm rounded-2xl p-6"
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
        <h2 className="text-base font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Members Only</h2>
        <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>Login with your Whop account to access the platform</p>

        <button
          data-testid="whop-login-btn"
          onClick={handleWhopLogin}
          disabled={whopLoading}
          className="w-full h-12 flex items-center justify-center gap-2 font-semibold text-sm tracking-wide transition-all disabled:opacity-50 rounded-xl"
          style={{
            background: 'linear-gradient(135deg, var(--accent-teal), var(--accent-cyan))',
            color: '#0B0F1A',
            boxShadow: '0 0 24px rgba(0, 229, 168, 0.15)',
          }}>
          {whopLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Login with Whop"}
        </button>

        <p className="mt-4 text-sm text-center" style={{ color: 'var(--text-secondary)' }}>
          Not a member?{" "}
          <a href="https://whop.com/probability-lab/probability-lab-free-access/" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-semibold transition-colors"
            style={{ color: 'var(--accent-teal)' }}>
            Join Probability Lab <ExternalLink className="w-3 h-3" />
          </a>
        </p>
      </div>
    </div>
  );
};

export default Login;
